
import React, { useState, useContext, useEffect } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route
} from "react-router-dom";
import { Container, useMediaQuery, useTheme,  ThemeProvider, createTheme } from "@mui/material";
import CssBaseline from '@mui/material/CssBaseline';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Optimus from './static/fonts/OptimusPrinceps.ttf';
import { initializeApp } from 'firebase/app';

import Game from './types/game';
import Run from './types/run';
import Menu from './components/Menu/Menu';


import GamesContext from './store/games-context';
import RunsContext from './store/runs-context';
import ColorModeContext from './store/color-context';
import UserContext, { UserContextType } from './store/user-context';

import Form from './views/AddRun/AddRun';
import Edit from './views/Edit/Edit';
import Timeline from './views/Timeline/Timeline';
import Statistics from './views/Statistics/Statistics';
import Visual from './views/Statistics/Visual';


import firebaseConfig from './config/firebase.config';

import './App.css';

export default function App() {
  
  const gamesContext = useContext(GamesContext);
  const runsContext = useContext(RunsContext);
  const colorMode = useContext(ColorModeContext);
  const userContext: UserContextType = useContext(UserContext);
  const theme = React.useMemo(() => {
    return createTheme({
      typography: {
        fontFamily: 'Optimus, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue'
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: `
            @font-face {
              font-family: 'Optimus';
              font-style: normal;
              font-display: swap;
              font-weight: 400;
              src: local('OptimusPrinceps'), url(${Optimus}) format('ttf');
              unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
            }
            `,
        }
      },
      palette: {
        mode: colorMode.colorMode
      },
    })
  }, [colorMode]);;

  const [firebaseInitialized, setFirebaseInitialized] = useState(false);

  if (!firebaseInitialized && userContext) {
    const firebaseApp = initializeApp(firebaseConfig);
    userContext?.setFirebaseApp?.(firebaseApp);
    setFirebaseInitialized(true);
  }

  useEffect(() => {
    if (!gamesContext.games || gamesContext.games.length <= 0) {
      fetch(`${firebaseConfig.databaseURL}${firebaseConfig.endpoints.games}`)
        .then(response => response.json())
        .then(gamesResponse => {
          const games: Game[] = (Object.values(gamesResponse) as Game[]);
          const sortedGames = games.sort((a: Game, b: Game) => (a.id - b.id));
          gamesContext.setGames?.(sortedGames);
        });
    }
    if (!runsContext.runs || runsContext.runs.length <= 0) {
      fetch(`${firebaseConfig.databaseURL}${firebaseConfig.endpoints.runs}`)
        .then(response => response.json())
        .then(runsResponse => {
          if (runsResponse && runsContext.setRuns) {
            const runs: Run[] = (Object.values(runsResponse) as Run[]);
            const runIds = (Object.keys(runsResponse) as string[]);
            runs.forEach((run, index) => run._id = runIds[index]);
            runsContext?.setRuns(runs);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  if (!firebaseInitialized || !userContext.firebaseApp || !("name" in userContext.firebaseApp)) {
    return (<>Loading...</>);
  }
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline/>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Router>
          <Menu />
          <Container maxWidth="xl" color="default">
            <Switch>
              <Route exact path="/">
                <Timeline />
              </Route>
              <Route path="/form" >
                <Form />
              </Route>
              <Route path="/edit" >
                <Edit />
              </Route>
              <Route path="/statistics">
                <Statistics />
              </Route>
              <Route path="/visual">
                <Visual />
              </Route>
            </Switch>
          </Container>
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
